"use strict";

import _ from "lodash";

/**
 * Get Valuetype of given Value
 * @param {*} value Arbitrary value whose valuetype will be resolved
 * @return {boolean} Valuetype of the given value.
 * @throws {Error} If the value does not conform to one of the allowed valuetypes.
 */
function getValuetype( value )
{
	let valuetype = typeof value;
	switch( valuetype )
	{
		case 'boolean':
		case 'string':
			return valuetype;
		case 'number':
			if( !isFinite( value ) )
				throw new Error( "Number value is Infinite or NaN" );
			return valuetype;
		case 'object':
			if( value === null )
				return 'null';
			else if( value instanceof Array )
				return 'array';
			else if( !isBasicObject( value ) )
				throw new Error( "Value is a non-basic object; this is not allowed!" );
			else if( value.hasOwnProperty( '$date' ) )
			{
				if( Object.keys( value ).length === 1 && typeof value.$date === 'number' && value.$date % 1 === 0 && isFinite( value.$date ) )
					return 'date';
				throw new Error( "Invalid date object! Must have only 1 field '$date' with integer value." );
			}
			else if( value.hasOwnProperty( '$ref' ) )
			{
				for( let key in value )
					if( key !== '$ref' && key !== '$source' )
						throw new Error( `Reference ($ref) value has invalid property: ${key}` );
				return 'reference';
			}
			else
				return 'object';
		default:
			throw new Error( "Unsupported type: "+ valuetype );
	}
}

function isBasicObject( value )
{
	if( typeof value !== 'object' || value === null )
		return false;
	if( Object.prototype.toString.call( value ) !== "[object Object]" )
		return false;
	if( value.constructor && typeof value.constructor === 'object' && !Object.prototype.hasOwnProperty.call( value, 'constructor' ) && value.constructor !== Object && value.constructor.name !== "Object" /* To work with safe-eval */ )
		return false;
	return true;	// Close enough for now; NOTE: gotta make sure to ONLY process own properties!
};


function validator_novalue( { value, config = { }, path = "" } )
{
	if( typeof value === 'undefined' )
		return { result : true };
	return { path, result: false, message : 'No value of any kind is allowed.' };
}

function validator_flag( { value, config = { }, path = "" } )
{
	let result = { result : true };
	if( value === false )
		result = { path, result : false, message : "Flags cannot have a [false] value. They are either [true], or do not exist. To clear a flag, delete it instead." };
	else if( value !== true )
		result = { path, result : false, message : "Incorrect value type at this field! Must be a boolean and only of value [true]." };
	return result;
}

function validator_regex( { value, config = { }, path = "" } )
{
	try {
		new RegExp( value );
		return { result : true };
	} catch( err ) {
		return { path, result : false, message : 'Invalid regular expression!' };
	}
}

function validator_enum( { value, config = { }, path = "" } )
{
	if( !config.possiblevalues.includes( value ) )
		return { path, result : false, message : 'Value not found in the list of allowed ones.' };
	return { result : true };
}

function validator_valuetype( { value, config = { }, path = "" } )
{
	return validateValue( { value, valuetype : 'string', validators : config.validators, path } );
}

function validator_terminalvaluetype( { value, config = { }, path = "" } )
{
	return validateValue( { value, valuetype : 'string', validators : config.validators, path } );
}

function validator_integer( { value, config = { }, path = "" } )
{
	if( value % 1 !== 0 )
		return { path, result : false, message : 'NOT a whole number!' };
	else
		return runValidator( { value, valuetype : 'number', validator : { '$schema' : '$validatorinclude.', 'validator' : '$number', 'config' : config }, path } );
}

function validator_number( { value, config = { }, path = "" } )
{
	let failMessage;
	if( !isFinite( value ) )
		failMessage = 'NaN or Infinite Value!';
	else if( config )
	{
		if( config.precision && value != value.toFixed( config.precision ) )
			failMessage = `Decimal digit precision is too high. There cannot be more than ${config.precision} decimal places.`;
		else if( config.min && ( ( config.minexclusive && value <= config.min ) || ( !config.minexclusive && value < config.min ) ) )
			failMessage = `Number is too low! Minimum value is ${config.min} (${(config.minexclusive ? 'exclusive' : 'inclusive')})`;
		else if( config.max && ( ( config.maxexclusive && value >= config.max ) || ( !config.maxexclusive && value > config.max ) ) )
			failMessage = `Number is too high! Maximum value is ${config.max} (${(config.maxexclusive ? 'exclusive' : 'inclusive')})`;
	}
	if( failMessage )
		return { path, result : false, message : failMessage };
	return { result : true };
}

function validator_string( { value, config = { }, path = "" } )
{
	let failMessage;
	if( config )
	{
		if( config.minlength && ( value.length < config.minlength ) )
			failMessage = `String is too short. Must be at least ${config.minlength} characters.`;
		else if( config.maxlength && ( value.length > config.maxlength ) )
			failMessage = `String is too long. Must be at most ${config.maxlength} characters.`;
		else if( config.regex )
		{
			let regex;
			try {
				regex = new RegExp( config.regex );
			} catch( err ) { throw new Error( 'Invalid regular expression from configuration!' ) }
			if( !regex.test( value ) )
				failMessage = 'String doesn\'t match the required regular expression format';
		}
	}
	if( failMessage )
		return { path, result : false, message : failMessage };
	return { result : true };
}
	
function validator_object( { value, config = { }, path = "" } )
{
	try {
		ensureValuetype( value, 'object' );
	} catch( err ) { return { path, result : false, message : err.message } }

	const keys = Object.keys( value );

	if( !config || !config.objectschema )
		return { result : true };
	else if( !value.$schema || !value.$schema.startsWith( config.objectschema ) )
		return { path, result : false, message : 'Object does not have the required schema definition.' };
	else if( typeof config.mincount === 'number' && keys.length < config.mincount )
		return { path, result : false, message : `This object does not have enough entries. Must have at least ${config.mincount}.` };
	else if( typeof config.maxcount === 'number' && keys.length > config.maxcount )
		return { path, result : false, message : `This object has too many entries. Must have at most ${config.maxcount}.` };
	if( config.keyformat )
	{
		const regex = new RegExp( config.keyformat );
		for( const key of keys )
			if( !regex.test( key ) )
				return { path, result : false, message : `The object has an invalid entry name/key: ${key}. Validation regex: ${regex}` };
	}
	if( config.itemvaluetype || ( config.itemvalidators && config.itemvalidators.length > 0 ) )
		return validateValueList( { values : Object.values( value ), validators : config.itemvalidators, valuetype : config.itemvaluetype, path } );

	return { result : true };
}

function validator_array( { value, config = { }, path = "" } )
{
	try {
		ensureValuetype( value, 'array' );
	} catch( err ) { return { path, result : false, message : err.message } }

	if( config )
	{
		if( config.mincount && value.length < config.mincount )
			return { path, result : false, message : 'Too few elements!' };
		else if( config.maxcount && value.length > config.maxcount )
			return { path, result : false, message : 'Too many elements!' };
		else if( config.itemvaluetype || ( config.itemvalidators && config.itemvalidators.length > 0 ) )
			return validateValueList( { values : value, validators : config.itemvalidators, valuetype : config.itemvaluetype, path } );
	}
	return { result : true };
}

function validator_schemareference( { value, config = { }, path = "" } )
{
	/* TODO: Must check that object is a schema. We can assume from ID */
	const res = { result : ( !config || !config.baseschema || value.startsWith( config.baseschema ) ) };
	if( !res.result )
		res.path = path;
	return res;
}

function validator_reference( { value, config, path = "" } )
{
	try {
		ensureValuetype( value, 'reference' );
	} catch( err ) { return { path, result : false, message : err.message } }

	const id = value.$ref;
	
	if( config.regex_id )
	{
		const regex = new RegExp( config.regex_id );
		if( !regex.test( id ) )
			return { path, result : false, message : `The reference ID [${id}] does not match the required format regex [${regex}]` };
	}
	if( Array.isArray( config.allowed_ids ) && !config.allowed_ids.includes( id ) )
		return { path, result : false, message : `The reference ID [${id}] is not in the list of allowed values [${config.allowed_ids.join( ',' )}]` };

	const { node, project, collection } = getSourceParts( value.$source );

	if( config.no_self_node && !node )
		return { path, result : false, message : `The $source must have an explicit node defined.` };
	if( config.no_self_project && !project )
		return { path, result : false, message : `The $source must have an explicit project defined.` };
	if( config.no_self_collection && !collection )
		return { path, result : false, message : `The $source must have an explicit collection defined.` };
	
	if( collection && config.regex_collection )
	{
		const regex = new RegExp( config.regex_collection );
		if( !regex.test( collection ) )
			return { path, result : false, message : `The collection name [${collection}] does not match the required format regex [${regex}]` };
	}
	if( collection && Array.isArray( config.allowed_collections ) && !config.allowed_collections.includes( collection ) )
		return { path, result : false, message : `The collection name [${collection}] is not in the list of allowed values [${config.allowed_collections.join( ',' )}]` };

	if( project && config.regex_project )
	{
		const regex = new RegExp( config.regex_project );
		if( !regex.test( project ) )
			return { path, result : false, message : `The project ID [${project}] does not match the required format regex [${regex}]` };
	}
	if( project && Array.isArray( config.allowed_collections ) && !config.allowed_projects.includes( project ) )
		return { path, result : false, message : `The project ID [${project}] is not in the list of allowed values [${config.allowed_projects.join( ',' )}]` };

	if( node && config.regex_node )
	{
		const regex = new RegExp( config.regex_node );
		if( !regex.test( node ) )
			return { path, result : false, message : `The node ID [${node}] does not match the required format regex [${regex}]` };
	}
	if( node && Array.isArray( config.allowed_collections ) && !config.allowed_nodes.includes( node ) )
		return { path, result : false, message : `The node ID [${node}] is not in the list of allowed values [${config.allowed_nodes.join( ',' )}]` };

	// TODO: Consider $schema match, based only on a $refschema field provided.. no retrieval of object, just a "this should be [X]" idea that app level can worry about.

	return { result : true };
}

function getSourceParts( source = "" )
{
	const parts = ( source || "" ).split( /\/:/g ).reverse( );
	return {
		node : ( parts.length > 2 ? parts[2] : "" ),
		project : ( parts.length > 1 ? parts[1] : "" ),
		collection : ( parts.length > 0 ? parts[0] : "" )
	};
}

// DISABLE THIS FOR NOW
// function validator_customcode( { value, config } )
// {
//	 $context.libs.code.execute( config.code, { value : value }, $callback );
// }
// module.exports.customcode = validator_customcode;

function ensureValuetype( value, valuetype )
{
	if( typeof valuetype === 'string' )
		valuetype = [ valuetype ];
	else if( !valuetype )
		valuetype = [ ];
	else if( !Array.isArray( valuetype ) )
		throw new Error( `Incorrect valuetype [${valuetype}] (type: ${typeof valuetype}).` );
	
	let thisValuetype;
	try {
		thisValuetype = getValuetype( value );
	} catch( err ) {
		throw new Error( `Valuetype check failed. Details: ${err.message}` );
	}

	if( valuetype.length > 0 && !valuetype.includes( thisValuetype ) )
		throw new Error( `Expected one of these valuetypes [${valuetype.join( ',' )}] but this value is [${thisValuetype}].` );
	
	return true;
}

function runValidator( { value, validator, path = "" } )
{
	const validatorName = validator.validator;
	if( !validatorHandlers.hasOwnProperty( validatorName ) )
		throw new TypeError( `Handler for validator [${validatorName}] could not be found!` );
	return validatorHandlers[validatorName]( { value, config : validator.config, path } );
}

function validateValue( { value, valuetype, validators = [], path = "" } )
{
	try {
		if( valuetype )
			ensureValuetype( value, valuetype );
		valuetype = undefined;
	} catch( err ) { return { path, result : false, message : err.message } }

	for( const validator of validators )
	{
		const result = runValidator( { value, valuetype, validator, path } );
		if( result.result !== true )
			return result;
	}
	return { result : true };
}

function validateValueList( { values = [], valuetype, validators = [], path = "" } )
{
	for( let i=0; i < values.length; i++ )
	{
		const value = values[i];
		const result = validateValue( { value, valuetype, validators, path: path +"/"+ i } );
		if( result.result !== true )
			return result;
	}
	return { result : true };
}

const validatorHandlers = {
	$novalue : validator_novalue,
	$flag : validator_flag,
	$regex : validator_regex,
	$enum : validator_enum,
	$valuetype : validator_valuetype,
	$terminalvaluetype : validator_terminalvaluetype,
	$number : validator_number,
	$integer : validator_integer,
 	$string : validator_string,
	$object : validator_object,
	$array : validator_array,
	$schemareference : validator_schemareference,
	$reference : validator_reference
	// FUTURE:
	// $date (min, max, etc.)
	// $logic (code -- For ad-hoc use; should be making custom validators for this use case)
	// $any (OR group)
	// $all (AND group; for use inside $any only)
	// $semantic (validate root, or any object as a whole with cross field checks -- code can do this too)
};

export {
	validateValue,
	validateValueList,
	runValidator,
	ensureValuetype,
	isBasicObject,
	getValuetype
};


// WEBPACK FOOTER //
// ./src/temp-validation/systemValidation.js